import { useState, useCallback } from 'react';
import type { ItineraryDay } from '../types';
import { directionsApi, type TravelMode } from '../api/directions.api';
import { toClientLeg, type ClientLeg } from '../utils/legFormatting';

export const DAY_ROUTE_COLORS = [
  '#14b8a6', // teal
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f97316', // orange
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

export interface AllDayLegs {
  /** Index in the original days[] array — used for color assignment and filtering */
  dayIdx: number;
  /** Stops the legs connect, in the order the server routed them. */
  activityIds: string[];
  legs: (ClientLeg | null)[];
}

interface Options {
  isLoaded: boolean;
  trip: { id: string };
}

/**
 * Every day's route at once. Stop order, waypoints and travel modes are decided
 * by the server (`POST /trips/:id/directions/route`) so web and mobile can't
 * drift apart — this hook only decodes polylines for the map to draw.
 *
 * Split by what each call can cost. `calculate` is the free pass: **always**
 * `cachedOnly`, by construction. ADR-0025: `resolveTrip` is the one call that
 * buys every day at once, and only the map, once per opening, makes it.
 * `resolveDay` and `recalculateDay` buy a single day.
 */
export function useAllDayRoutes({ isLoaded, trip }: Options) {
  const [allDayLegs, setAllDayLegs] = useState<AllDayLegs[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Whether the free pass has answered. Nothing may buy a day before it has:
   * the answer is about to arrive for free, and paying first buys legs the
   * cache already held.
   */
  const [cachedPassDone, setCachedPassDone] = useState(false);

  /** One day's answer replaces that day's entry and leaves the rest alone. */
  const spliceDay = useCallback((next: AllDayLegs) => {
    setAllDayLegs(prev => [...prev.filter(d => d.dayIdx !== next.dayIdx), next]
      .sort((a, b) => a.dayIdx - b.dayIdx));
  }, []);

  /** The free pass: what the server already has. A miss comes back null. */
  const calculate = useCallback(async (days: ItineraryDay[]) => {
    if (!isLoaded) return;

    setLoading(true);
    setError(null);
    setAllDayLegs([]);
    setCachedPassDone(false);

    let routeDays;
    try {
      routeDays = await directionsApi.route(trip.id, { cachedOnly: true });
    } catch {
      setLoading(false);
      setError('Could not calculate routes.');
      return;
    }

    const dayIdxById = new Map(days.map((d, i) => [d.id, i]));
    setAllDayLegs(routeDays.flatMap(rd => {
      const dayIdx = dayIdxById.get(rd.dayId);
      if (dayIdx == null) return [];
      return [{
        dayIdx,
        activityIds: rd.activityIds,
        legs: rd.legs.map(leg => leg && toClientLeg(leg)),
      }];
    }));
    setCachedPassDone(true);
    setLoading(false);
  }, [isLoaded, trip.id]);

  /**
   * ADR-0025: every day's missing legs in one call — the map shows the whole
   * route as it opens. One request for the trip, never one per day in a loop.
   */
  const resolveTrip = useCallback(async (days: ItineraryDay[]): Promise<void> => {
    if (!isLoaded) return;
    let routeDays;
    try {
      routeDays = await directionsApi.route(trip.id);
    } catch {
      return;
    }
    const dayIdxById = new Map(days.map((d, i) => [d.id, i]));
    setAllDayLegs(routeDays.flatMap(rd => {
      const dayIdx = dayIdxById.get(rd.dayId);
      if (dayIdx == null) return [];
      return [{ dayIdx, activityIds: rd.activityIds, legs: rd.legs.map(leg => leg && toClientLeg(leg)) }];
    }));
  }, [isLoaded, trip.id]);

  /** The paid half for one day: the day just put on screen, or recalculated. */
  const resolveDay = useCallback(async (dayIdx: number, dayId: string): Promise<boolean> => {
    if (!isLoaded) return false;

    let day;
    try {
      [day] = await directionsApi.route(trip.id, { dayId });
    } catch {
      return false;
    }
    if (!day) return false;

    spliceDay({
      dayIdx,
      activityIds: day.activityIds,
      legs: day.legs.map(leg => leg && toClientLeg(leg)),
    });
    return true;
  }, [isLoaded, trip.id, spliceDay]);

  /**
   * Recompute a single day and splice it back in. A mode change or a drag only
   * affects its own day, and recalculating the whole trip would blank every
   * other line on the map for as long as the round trip takes — for legs that
   * are already cached and would come back identical.
   */
  const recalculateDay = useCallback(async (
    dayIdx: number, dayId: string, activityIds: string[], modes: (TravelMode | null)[],
  ): Promise<AllDayLegs | null> => {
    if (!isLoaded) return null;

    let day;
    try {
      [day] = await directionsApi.route(trip.id, { dayId, activityIds, modes });
    } catch {
      return null;
    }
    if (!day) return null;

    const next: AllDayLegs = {
      dayIdx,
      activityIds: day.activityIds,
      legs: day.legs.map(leg => leg && toClientLeg(leg)),
    };
    spliceDay(next);
    return next;
  }, [isLoaded, trip.id, spliceDay]);

  const reset = useCallback(() => {
    setAllDayLegs([]);
    setError(null);
    setLoading(false);
    setCachedPassDone(false);
  }, []);

  return { allDayLegs, loading, error, cachedPassDone, calculate, resolveTrip, resolveDay, recalculateDay, reset };
}
